import { formatDateOnlyLocal } from '@/lib/dateHelpers';

import { hhmmToMinutes } from './sessionDates';
import type { GroupEnrollmentMode, GroupSetupFormValues } from './types';

export type GroupValidationError = {
  field: keyof GroupSetupFormValues | 'general';
  message: string;
};

function todayDateOnly(): string {
  return formatDateOnlyLocal(new Date());
}

/** Extra activation-only checks (PRD §3) beyond draft setup validation. */
function activationOnlyErrors(values: GroupSetupFormValues): GroupValidationError[] {
  const errors: GroupValidationError[] = [];
  if (!values.timezone?.trim()) {
    errors.push({ field: 'timezone', message: 'Timezone is required to activate.' });
  }
  if (!values.modality) {
    errors.push({ field: 'modality', message: 'Modality is required to activate.' });
  }
  if (
    (values.modality === 'in-person' || values.modality === 'hybrid') &&
    !values.locationReference?.trim()
  ) {
    errors.push({
      field: 'locationReference',
      message: 'Location is required for in-person or hybrid groups.',
    });
  }
  return errors;
}

/**
 * Activation checklist (PRD §3). New groups stay inactive until these pass.
 */
export function validateGroupActivation(
  values: GroupSetupFormValues,
): GroupValidationError[] {
  return validateGroupSetupForm(values, { forActivation: true });
}

/** True when the form meets every requirement to activate (Save & Activate). */
export function isGroupSetupReadyToActivate(values: GroupSetupFormValues): boolean {
  return validateGroupActivation({ ...values, active: true }).length === 0;
}

export function validateGroupSetupForm(
  values: GroupSetupFormValues,
  opts?: { requireImage?: boolean; forActivation?: boolean },
): GroupValidationError[] {
  const errors: GroupValidationError[] = [];

  if (!values.name.trim()) {
    errors.push({ field: 'name', message: 'Group name is required.' });
  }
  if (!values.templateReference?.trim()) {
    errors.push({
      field: 'templateReference',
      message: 'Group Template is required.',
    });
  }
  if (!values.organizationId?.trim()) {
    errors.push({ field: 'organizationId', message: 'Practice is required.' });
  }
  if (!values.primaryFacilitatorReference?.trim()) {
    errors.push({
      field: 'primaryFacilitatorReference',
      message: 'Primary facilitator is required.',
    });
  }
  if (!values.minEnrollment || values.minEnrollment <= 0) {
    errors.push({ field: 'minEnrollment', message: 'Minimum enrollment must be greater than 0.' });
  }
  if (!values.maxEnrollment || values.maxEnrollment <= 0) {
    errors.push({ field: 'maxEnrollment', message: 'Maximum enrollment must be greater than 0.' });
  }
  if (values.maxEnrollment < values.minEnrollment) {
    errors.push({
      field: 'maxEnrollment',
      message: 'Maximum enrollment must be at least the minimum enrollment.',
    });
  }
  if (opts?.requireImage && !values.imageUrl) {
    errors.push({ field: 'imageUrl', message: 'Group image is required.' });
  }

  // Schedule fields are required when activating or when the cohort is already active.
  // Edit-details may save without a schedule.
  const requireSchedule = opts?.forActivation || values.active;
  if (requireSchedule) {
    if (!values.startDate) {
      errors.push({ field: 'startDate', message: 'Start date is required.' });
    } else if (!values.id && values.startDate < todayDateOnly()) {
      errors.push({
        field: 'startDate',
        message: 'Start date must be today or a future date.',
      });
    }
    if (!values.startTime) {
      errors.push({ field: 'startTime', message: 'Start time is required.' });
    }
    if (!values.endTime) {
      errors.push({ field: 'endTime', message: 'End time is required.' });
    } else {
      const start = hhmmToMinutes(values.startTime);
      const end = hhmmToMinutes(values.endTime);
      if (start != null && end != null && end <= start) {
        errors.push({ field: 'endTime', message: 'End time must be after start time.' });
      }
    }
    validateEnrollmentModeFields(values, errors);
  }

  if (opts?.forActivation || values.active) {
    errors.push(...activationOnlyErrors(values));
  }

  return dedupeErrors(errors);
}

/** One message per field+text — callers combine checklists and must not show repeats. */
function dedupeErrors(errors: GroupValidationError[]): GroupValidationError[] {
  const seen = new Set<string>();
  return errors.filter((e) => {
    const key = `${e.field}::${e.message}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function validateEnrollmentModeFields(
  values: GroupSetupFormValues,
  errors: GroupValidationError[],
): void {
  const mode = values.enrollmentMode;
  const recurrence = values.recurrence ?? 'weekly';

  if (recurrence === 'weekly' || recurrence === 'biweekly') {
    if (!values.daysOfWeek.length) {
      errors.push({ field: 'daysOfWeek', message: 'Select at least one day of the week.' });
    }
  } else if (recurrence === 'custom') {
    if (!(values.sessionDates ?? []).length) {
      errors.push({
        field: 'sessionDates',
        message: 'Select at least one session date on the calendar.',
      });
    }
  }

  if (mode === 'closed') {
    if (!values.endDate) {
      errors.push({ field: 'endDate', message: 'End date is required for closed groups.' });
    }
    const lockDate = values.enrollmentLockDate?.trim() || undefined;
    if (values.startDate && values.endDate && values.endDate <= values.startDate) {
      errors.push({ field: 'endDate', message: 'End date must be after the start date.' });
    }
    if (lockDate && values.startDate && lockDate > values.startDate) {
      errors.push({
        field: 'enrollmentLockDate',
        message: 'Enrollment lock date must be on or before the start date.',
      });
    }
    if (!values.id && lockDate && lockDate < todayDateOnly()) {
      errors.push({
        field: 'enrollmentLockDate',
        message: 'Enrollment lock date must be today or a future date.',
      });
    }
  }

  if (mode === 'open') {
    if (values.endDate && values.startDate && values.endDate <= values.startDate) {
      errors.push({ field: 'endDate', message: 'End date must be after the start date.' });
    }
  }
}

export function canEnrollPatientInGroup(
  mode: GroupEnrollmentMode,
  enrollmentLocked: boolean,
  enrolledCount: number,
  maxEnrollment: number,
  hasOverride = false,
  opts?: {
    /** Enrollment needs a cleared screening outcome (required for every Group). */
    screeningComplete?: boolean;
    /** ServiceRequest tracker stage readiness (matching / fulfilled). */
    trackerStage?: string;
  },
): boolean {
  if (enrolledCount >= maxEnrollment) return false;
  if (mode === 'closed' && enrollmentLocked && !hasOverride) return false;
  if (opts && opts.screeningComplete === false && !hasOverride) return false;
  if (
    opts?.trackerStage &&
    !['matching', 'fulfilled', 'consultation'].includes(opts.trackerStage) &&
    !hasOverride
  ) {
    return false;
  }
  return true;
}
